import { useEffect, useRef, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { getCookie, eraseCookie } from '../../api/helpers';
import Auth from '../../api/auth';
import { updateLastActivity, setInactivityLogoutCallback } from '../../helpers/axiosApi';

const useRefreshToken = (onInactivityLogout) => {
  const refreshTimeoutRef = useRef(null);
  const activityTimeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());
  const isRefreshingRef = useRef(false);

  const clearTimeouts = useCallback(() => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
      activityTimeoutRef.current = null;
    }
  }, []);

  const scheduleTokenRefresh = useCallback(() => {
    const token = getCookie('access');
    if (!token) return;

    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - now;
      
      // Refresh 2 minutes before expiry
      const refreshTime = Math.max(0, (timeUntilExpiry - 120) * 1000);
      
      clearTimeouts();
      
      refreshTimeoutRef.current = setTimeout(async () => {
        // Check if user has been inactive before refreshing
        const now = Date.now();
        const inactivityTime = now - lastActivityRef.current;
        const maxInactivityTime = 15 * 60 * 1000; // 15 minutes
        
        if (inactivityTime > maxInactivityTime) {
          eraseCookie("access");
          eraseCookie("refresh");
          clearTimeouts();
          if (onInactivityLogout) {
            onInactivityLogout();
          }
          return;
        }
        
        try {
          console.log('🔄 [SCHEDULED-REFRESH] Refreshing token before expiry...');
          await Auth.refreshToken();
          console.log('✅ [SCHEDULED-REFRESH] Token refreshed successfully');
          scheduleTokenRefresh(); // Schedule next refresh
        } catch (error) {
          console.error('❌ [SCHEDULED-REFRESH] Failed to refresh token:', error);
        }
      }, refreshTime);
      
      
    } catch (error) {
      console.error('Invalid token:', error);
    }
  }, [clearTimeouts]);

  const refreshTokenIfNeeded = useCallback(async () => {
    const token = getCookie('access');
    if (!token) return;

    if (isRefreshingRef.current) {
      return;
    }

    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;
      const timeUntilExpiry = decoded.exp - now;
      
      // If token expires within 2 minutes, refresh it now
      if (timeUntilExpiry <= 120) {
        console.log('🔄 [ACTIVITY-REFRESH] Token needs refresh due to activity, refreshing now...');
        isRefreshingRef.current = true;
        await Auth.refreshToken();
        console.log('✅ [ACTIVITY-REFRESH] Token refreshed due to activity');
        // After refresh, schedule the next refresh
        scheduleTokenRefresh();
      }
    } catch (error) {
      console.error('Error checking token expiry:', error);
    } finally {
      isRefreshingRef.current = false;
    }
  }, []);

  const lastActivityCallRef = useRef(0);
  
  const handleUserActivity = useCallback(() => {
    const now = Date.now();
    
    // Throttle activity calls to once per second to prevent spam
    if (now - lastActivityCallRef.current < 1000) {
      return;
    }
    
    lastActivityCallRef.current = now;
    lastActivityRef.current = now;
    updateLastActivity(); // Update global activity tracker
    
    // Clear existing activity timeout
    if (activityTimeoutRef.current) {
      clearTimeout(activityTimeoutRef.current);
    }
    
    // Set new activity timeout (15 minutes)
    activityTimeoutRef.current = setTimeout(() => {
      eraseCookie("access");
      eraseCookie("refresh");
      clearTimeouts();
      if (onInactivityLogout) {
        onInactivityLogout();
      }
    }, 15 * 60 * 1000); // 15 minutes
    
    // Refresh token immediately if needed due to activity
    refreshTokenIfNeeded();
  }, [refreshTokenIfNeeded, clearTimeouts, onInactivityLogout]);

  useEffect(() => {
    // Set the global logout callback
    setInactivityLogoutCallback(onInactivityLogout);
    
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    
    // Add activity listeners
    events.forEach(event => {
      document.addEventListener(event, handleUserActivity, true);
    });

    // Initial setup
    handleUserActivity();

    return () => {
      // Cleanup
      events.forEach(event => {
        document.removeEventListener(event, handleUserActivity, true);
      });
      clearTimeouts();
      setInactivityLogoutCallback(null);
    };
  }, [handleUserActivity, clearTimeouts, onInactivityLogout]);

  return null;
};

export default useRefreshToken;